import { theme } from 'antd';

import type { MappingAlgorithm } from 'antd';

const overridePresetShades: MappingAlgorithm = (_seedToken, mapToken) =>
  ({
    ...mapToken,
    green1: '#1E3029',
    green2: '#24392F',
    green3: '#2A4236',
    green6: '#62C88E',
    greenHover: '#62C88E',
    greenActive: '#62C88E',
    blue1: '#212A3C',
    blue2: '#263247',
    blue3: '#2C3A52',
    blue6: '#94B4F5',
    blueHover: '#94B4F5',
    blueActive: '#94B4F5',
    purple1: '#2B273E',
    purple2: '#332E49',
    purple3: '#3A3454',
    purple6: '#C098EB',
    purpleHover: '#C098EB',
    purpleActive: '#C098EB',
    orange1: '#392821',
    orange2: '#442E25',
    orange3: '#4F3429',
    orange6: '#F0965A',
    orangeHover: '#F0965A',
    orangeActive: '#F0965A',
    yellow1: '#382E20',
    yellow2: '#423625',
    yellow3: '#4C3E29',
    yellow6: '#DEAC5C',
    yellowHover: '#DEAC5C',
    yellowActive: '#DEAC5C',
    red1: '#332024',
    red2: '#3D2429',
    red3: '#47292E',
    red6: '#EB7878',
    redHover: '#EB7878',
    redActive: '#EB7878',
    cyan1: '#1C2C2F',
    cyan2: '#213437',
    cyan3: '#263C3F',
    cyan6: '#6CBEE6',
    cyanHover: '#6CBEE6',
    cyanActive: '#6CBEE6',
    magenta1: '#321F2C',
    magenta2: '#3B2333',
    magenta3: '#44273A',
    magenta6: '#E882B6',
    magentaHover: '#E882B6',
    magentaActive: '#E882B6',
    blue5: '#407CE2',
    blue7: '#94B4F5',
    cyan5: '#208C8C',
    cyan7: '#54C4BE',
    green5: '#2C945B',
    green7: '#62C88E',
    yellow5: '#BA8224',
    yellow7: '#DEAC5C',
    orange5: '#D66E2C',
    orange7: '#F0965A',
    purple5: '#8166DE',
    purple7: '#C098EB',
    pink5: '#C83C82',
    pink7: '#E882B6',
    red5: '#BA3D3D',
    red7: '#EB7878',
    geekblue5: '#5A5AD0',
    geekblue7: '#A3A3F0',
    lime5: '#7F9C34',
    lime7: '#B3CC6A',
    volcano5: '#96684A',
    volcano7: '#C9A183',

    colorPrimary: '#E2E2E6',
    colorPrimaryHover: '#EFEFF2',
    colorPrimaryActive: '#D2D2D8',
    colorPrimaryBg: '#2D2D34',
    colorPrimaryBorder: '#383840',
    colorPrimaryText: '#E2E2E6',

    colorSuccess: '#62C88E',
    colorSuccessHover: '#7AD4A0',
    colorSuccessActive: '#4FB57C',
    colorSuccessBg: '#1E3029',
    colorSuccessBgHover: '#24392F',
    colorSuccessBorder: '#214232',
    colorSuccessText: '#62C88E',
    colorSuccessTextHover: '#7AD4A0',
    colorSuccessTextActive: '#4FB57C',

    colorError: '#EB7878',
    colorErrorHover: '#F28E8E',
    colorErrorActive: '#D86666',
    colorErrorBg: '#302428',
    colorErrorBgHover: '#3D2429',
    colorErrorBorder: '#482527',
    colorErrorText: '#EB7878',
    colorErrorTextHover: '#F28E8E',
    colorErrorTextActive: '#D86666',

    colorWarning: '#DEAC5C',
    colorWarningHover: '#E9BC74',
    colorWarningActive: '#C99A4B',
    colorWarningBg: '#382E20',
    colorWarningBgHover: '#423625',
    colorWarningBorder: '#4B3A21',
    colorWarningText: '#DEAC5C',
    colorWarningTextHover: '#E9BC74',
    colorWarningTextActive: '#C99A4B',

    colorInfo: '#94B4F5',
    colorInfoHover: '#A8C3F8',
    colorInfoActive: '#7FA3EE',
    colorInfoBg: '#212A3C',
    colorInfoBgHover: '#263247',
    colorInfoBorder: '#26385A',
    colorInfoText: '#6CBEE6',
    colorInfoTextHover: '#85CBEC',
    colorInfoTextActive: '#57ACD6',
  }) as ReturnType<MappingAlgorithm>;

export const darkTheme = {
  algorithm: [theme.darkAlgorithm, overridePresetShades],
  cssVar: { prefix: 'ant' },
  token: {
    fontFamily: "'Inter Variable', Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    fontFamilyCode: "'JetBrains Mono Variable', 'JetBrains Mono', monospace",

    colorBgBase: '#1B1B1F',
    colorTextBase: '#F0F0F4',

    colorLink: '#F0F0F4',
    linkDecoration: 'underline',
    linkHoverDecoration: 'underline',

    colorText: '#F0F0F4',
    colorTextSecondary: '#D2D2D8',
    colorTextTertiary: '#9898A2',
    colorTextQuaternary: '#7C7C86',
    colorTextPlaceholder: '#7C7C86',
    colorTextLightSolid: '#18181B',
    colorWhite: '#18181B',

    colorBgContainer: '#1B1B1F',
    colorBgElevated: '#222227',
    colorBgLayout: '#17151A',
    colorBgSpotlight: '#F0F0F4',
    colorBgMask: 'rgba(9, 9, 11, 0.55)',
    colorFillAlter: '#222227',

    colorBorder: '#383840',
    colorBorderSecondary: '#2D2D34',
    colorSplit: '#29292F',

    colorFill: 'rgba(255, 255, 255, 0.14)',
    colorFillSecondary: 'rgba(255, 255, 255, 0.09)',
    colorFillTertiary: 'rgba(255, 255, 255, 0.07)',
    colorFillQuaternary: 'rgba(255, 255, 255, 0.04)',

    boxShadowTertiary: '0 1px 2px rgba(9, 9, 11, 0.30)',
    boxShadowSecondary: '0 12px 32px rgba(9, 9, 11, 0.45)',
    boxShadow: '0 12px 32px rgba(9, 9, 11, 0.55)',

    lineWidthFocus: 0,
  },
  components: {
    Button: {
      fontWeight: 500,
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      cyan6: '#54C4BE',
      cyanHover: '#64D0CA',
      cyanActive: '#48B0AA',
    },
    Dropdown: { borderRadiusLG: 16, borderRadiusSM: 12, colorTextLightSolid: '#FFFFFF' },
    Popover: { borderRadiusLG: 16 },
    Tooltip: { colorBgSpotlight: '#F0F0F4', colorTextLightSolid: '#18181B' },
    Switch: { handleBg: '#1B1B1F' },
    Input: { colorFillTertiary: '#2D2D34', colorFillSecondary: '#2D2D34', activeBg: '#2D2D34' },
    InputNumber: {
      colorFillTertiary: '#2D2D34',
      colorFillSecondary: '#2D2D34',
      activeBg: '#2D2D34',
    },
    Mentions: { colorFillTertiary: '#2D2D34', colorFillSecondary: '#2D2D34', activeBg: '#2D2D34' },
    DatePicker: {
      colorFillTertiary: '#2D2D34',
      colorFillSecondary: '#2D2D34',
      activeBg: '#2D2D34',
    },
    Select: {
      colorFillTertiary: '#2D2D34',
      colorFillSecondary: '#2D2D34',
      borderRadiusLG: 16,
      borderRadiusSM: 12,
    },
    TreeSelect: { colorFillTertiary: '#2D2D34', colorFillSecondary: '#2D2D34' },
    Cascader: { colorFillTertiary: '#2D2D34', colorFillSecondary: '#2D2D34' },
  },
};
